import React from 'react';
import { makeStyles, createStyles } from '@material-ui/core';
import { Link, InlineText, ExtendedTheme, TextBlock } from 'neon-design-system';
import { Article, ArticleSummary, Channel, IdAndName, ListItem, Publisher } from '../../shared/types';
import { formatDisplayDateTime } from '../../utils/date.utils';
import { getPublisherUrl } from '../../utils/utils';
import { getChannelLink } from '../../utils/link.utils';

type StyleName = 'caption1' | 'caption2';

export interface PublishedByProps {
	date: Date;
	publisher: ListItem;
	className?: string;
	styleName?: StyleName;
	momentInTimeFormat: boolean;
	showEditDate: boolean;
	editDate?: Date;
	simplified?: boolean;
	channel?: Channel;
	disabledPublishedDate?: boolean;
}

const useStyles = makeStyles<ExtendedTheme, {styleName: StyleName}>(theme => createStyles({
	root: {
		[theme.breakpoints.down('xs')]: {
			'& a': {
				fontSize: 10
			}
		},
		[theme.breakpoints.up('md')]: {
			marginBottom: theme.spacing(2)
		},
		[theme.breakpoints.up('xl')]: {
			'& a': {
				fontSize: 12
			}
		}
	},
	link: ({ styleName }) => ({
		...(
			styleName === 'caption1' ? theme.typography.caption :
			styleName === 'caption2' ? theme.typography.overline :
			{}
		),
		fontWeight: 'bold'
	}),
	bullet: {
		display: 'inline-block',
		height: '4px',
		width: '4px',
		backgroundColor: theme.palette.grey[400],
		borderRadius: '2px',
		marginLeft: '8px',
		marginRight: '8px',
		marginBottom: '2px'
	}
}));

const PublishedBy = (props: PublishedByProps): JSX.Element => {
	const { styleName, date, momentInTimeFormat, showEditDate, editDate, publisher, simplified, channel, disabledPublishedDate } = props;
	const formattedStyleName = styleName ?? 'caption2';
	const classes = useStyles({ styleName: formattedStyleName });
	const dateString = formatDisplayDateTime(date, momentInTimeFormat);

	const channelData = channel ? (
		<>
			{' '}in{' '}
			<Link to={getChannelLink(channel._id)} className={classes.backLink}>
				<strong>{channel.name}</strong>
			</Link>
		</>
	) : <></>

	const editDisplay = disabledPublishedDate && showEditDate && editDate &&
		<span>
			<div className={classes.bullet}/>Last edited{' '}{formatDisplayDateTime(editDate, momentInTimeFormat)}
		</span>

	if (simplified) {
		return (
			<TextBlock
				styleName="footnote"
				ink="muted"
			>
				{dateString} <> by </><Link id="published-by-link" to={publisher.url}><b>{publisher.name}</b></Link>
			</TextBlock>
		)
	}

	return (
		<InlineText
			color={'grey'}
			shade={400}
			sm="medium"
			xs="small"
			xl="large"
			styleName={formattedStyleName} className={[classes.root, props.className].join(' ')}
		>
			Published {disabledPublishedDate ? dateString : ''}
			{channelData}
			{' '}by{' '}
			<Link id="published-by-link" className={classes.link} to={publisher.url}>{publisher.name}</Link>
			{editDisplay}
		</InlineText>
	)

};

export default PublishedBy;


export function getPublishedData(article: ArticleSummary | Article, sectionName: string, momentInTimeFormat: boolean, showEditDate: boolean): PublishedByProps {
	return getPublishedDataByFields(article.publishDate, article.publishedBy, article.categories, sectionName, momentInTimeFormat, showEditDate, article.lastEdited );
}

function getPublishedDataByFields(publishDate: Date | undefined, publishedBy: Publisher, categories: IdAndName[], sectionName: string, momentInTimeFormat: boolean, showEditDate: boolean, editDate: Date | undefined  ): PublishedByProps {
	const dt = publishDate ? new Date(publishDate) : new Date();
	return {
		date: dt,
		publisher: entityToListItem(publishedBy._id, publishedBy.name, getPublisherUrl(publishedBy)),
		momentInTimeFormat: momentInTimeFormat,
		showEditDate: showEditDate,
		editDate: editDate
	};
}

function entityToListItem(id: string, name: string, urlPrefix: string): ListItem {
	return {
		id,
		name,
		url: urlPrefix
	};
}
